/**
 * PhoneVerificationStep — OTP gate used by both TrackClaimPage and InviteLanding.
 *
 * Flow:
 *   phone  → user enters E.164 number + optional channel toggle
 *   code   → 6-digit OTP input; "resend" re-runs /verify/start
 *   notify → verified ✓ + opt-in checkbox for race-result notifications
 *
 * Returning sailor skip:
 *   Pass existingVerifiedPhone to render a "already verified" pill that lets
 *   the user proceed without re-verifying (onVerified called with no token —
 *   the backend skips enforcement for returning sailors when their profile
 *   already has phoneVerifiedAt).
 *
 * CAPTCHA:
 *   When VITE_TURNSTILE_SITE_KEY is set, the Cloudflare Turnstile widget
 *   renders before the send step. In dev (no key), a bypass token is used;
 *   the backend's validateCaptcha is a no-op outside Lambda.
 */

import { useState, useRef, useEffect } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PhoneVerificationResult {
  /** Short-lived HMAC proof to include in the claim body. Absent when skipped. */
  verifyToken?: string;
  notifyOptIn: boolean;
  /** The exact consent text the user agreed to (GDPR audit trail). */
  notifyConsentText?: string;
}

export interface PhoneVerificationStepProps {
  /** Called when the step is complete (verified or skipped). */
  onVerified: (result: PhoneVerificationResult) => void;
  /** Called when the user explicitly dismisses the step without verifying. */
  onSkip: () => void;
  /**
   * If set: show a "you're already verified" banner for this phone number
   * and allow proceeding without re-running the OTP.
   */
  existingVerifiedPhone?: string;
  className?: string;
}

type StepState =
  | 'phone'    // enter number + channel
  | 'sending'  // POST /verify/start in flight
  | 'code'     // enter OTP
  | 'checking' // POST /verify/check in flight
  | 'notify'   // verified ✓ — show opt-in then call onVerified
  | 'resending';

type Channel = 'sms' | 'whatsapp';

// SMS consent text (stored verbatim in notification_consent). Keep in sync with
// the label text rendered below — they must match exactly (GDPR / TCPA audit).
const CONSENT_TEXT_SMS =
  'I agree to receive race result notifications via SMS. Message frequency varies. ' +
  'Message and data rates may apply. Reply STOP to opt out.';

const CONSENT_TEXT_WHATSAPP =
  'I agree to receive race result notifications via WhatsApp. You can opt out at any time.';

const E164_RE = /^\+[1-9]\d{1,14}$/;

// ── Component ─────────────────────────────────────────────────────────────────

export function PhoneVerificationStep({
  onVerified,
  onSkip,
  existingVerifiedPhone,
  className,
}: PhoneVerificationStepProps) {
  const [step, setStep] = useState<StepState>('phone');
  const [phone, setPhone] = useState('');
  const [channel, setChannel] = useState<Channel>('sms');
  const [code, setCode] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [notifyOptIn, setNotifyOptIn] = useState(false);
  const [error, setError] = useState('');
  const codeRef = useRef<HTMLInputElement>(null);

  // Turnstile (Cloudflare CAPTCHA) — loaded lazily when site key is present.
  const turnstileRef = useRef<HTMLDivElement>(null);
  const captchaTokenRef = useRef<string>('dev-bypass');
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileRef.current) return;

    // Load Turnstile script once, then render widget.
    const id = 'turnstile-script';
    if (!document.getElementById(id)) {
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true;
      document.head.appendChild(s);
    }

    let widgetId: string | undefined;

    function tryRender() {
      if (!(window as unknown as { turnstile?: { render: (el: HTMLElement, opts: unknown) => string } }).turnstile?.render || !turnstileRef.current) {
        setTimeout(tryRender, 200);
        return;
      }
      widgetId = (window as unknown as { turnstile: { render: (el: HTMLElement, opts: unknown) => string } }).turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token: string) => { captchaTokenRef.current = token; },
        'expired-callback': () => { captchaTokenRef.current = ''; },
      });
    }
    tryRender();

    return () => {
      if (widgetId) {
        (window as unknown as { turnstile?: { remove: (id: string) => void } }).turnstile?.remove(widgetId);
      }
    };
  }, [turnstileSiteKey]);

  // Auto-focus code input when we enter the code step
  useEffect(() => {
    if (step === 'code') {
      setTimeout(() => codeRef.current?.focus(), 50);
    }
  }, [step]);

  // ── Returning sailor: already verified ────────────────────────────────────
  if (existingVerifiedPhone) {
    return (
      <div className={className} data-testid="phone-verify-returning">
        <div className="flex items-center gap-2 rounded-md bg-green-50 px-4 py-3 text-sm">
          <CheckIcon />
          <span className="font-medium text-green-800">
            Phone verified ({maskPhone(existingVerifiedPhone)})
          </span>
        </div>
      </div>
    );
  }

  // ── Notify / completion step ──────────────────────────────────────────────
  if (step === 'notify') {
    const consentText = channel === 'whatsapp' ? CONSENT_TEXT_WHATSAPP : CONSENT_TEXT_SMS;
    const channelLabel = channel === 'whatsapp' ? 'WhatsApp' : 'SMS';

    return (
      <div className={className} data-testid="phone-verify-notify">
        <div className="flex items-center gap-2 rounded-md bg-green-50 px-4 py-3 text-sm">
          <CheckIcon />
          <span className="font-medium text-green-800">
            Phone verified ({maskPhone(phone)})
          </span>
        </div>

        <div className="mt-3 flex items-start gap-3">
          <Checkbox
            id="notify-opt-in"
            checked={notifyOptIn}
            onCheckedChange={(v) => setNotifyOptIn(v === true)}
            className="mt-0.5"
            data-testid="notify-opt-in-checkbox"
          />
          <Label
            htmlFor="notify-opt-in"
            className="text-sm font-normal leading-snug text-muted-foreground"
          >
            Send me race results and replay links via {channelLabel}.
            <br />
            <span className="text-xs text-muted-foreground/70">
              {channel === 'sms'
                ? 'Message and data rates may apply. Reply STOP to opt out.'
                : 'You can opt out at any time.'}
            </span>
          </Label>
        </div>

        <Button
          type="button"
          className="mt-4 w-full bg-brand-blue !text-navy-900 hover:bg-brand-dark"
          onClick={() =>
            onVerified({
              verifyToken,
              notifyOptIn,
              notifyConsentText: notifyOptIn ? consentText : undefined,
            })
          }
          data-testid="phone-verify-done-button"
        >
          Continue
        </Button>
      </div>
    );
  }

  // ── Phone / code entry ────────────────────────────────────────────────────
  const isSending = step === 'sending' || step === 'resending';
  const isChecking = step === 'checking';

  async function handleSendCode() {
    setError('');
    if (!E164_RE.test(phone)) {
      setError('Please enter a valid phone number including country code, e.g. +14155551234');
      return;
    }
    setStep(step === 'resending' ? 'resending' : 'sending');
    try {
      await api.post(
        '/verify/start',
        { phone, channel, captchaToken: captchaTokenRef.current },
        { silent: true }
      );
      setCode('');
      setStep('code');
    } catch (err) {
      setStep('phone');
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Please wait an hour before trying again.');
      } else if (err instanceof ApiError && err.status === 400) {
        const msg = (err as ApiError & { body?: { message?: string } }).body?.message ?? '';
        setError(msg.includes('VoIP') ? 'VoIP numbers are not supported. Please use a mobile or landline.' : msg || 'Could not send code — please check the number and try again.');
      } else {
        setError('Could not send a code right now. Please try again in a moment.');
      }
    }
  }

  async function handleVerifyCode() {
    setError('');
    if (code.length < 4) {
      setError('Please enter the full verification code.');
      return;
    }
    setStep('checking');
    try {
      const res = await api.post<{ verifyToken: string }>(
        '/verify/check',
        { phone, code, channel },
        { silent: true }
      );
      setVerifyToken(res.verifyToken);
      setStep('notify');
    } catch (err) {
      setStep('code');
      if (err instanceof ApiError && err.status === 400) {
        setError('That code is incorrect or expired. Try again or request a new one.');
      } else {
        setError('Verification failed. Please try again.');
      }
    }
  }

  return (
    <div className={className} data-testid="phone-verify-step">
      {/* Phone entry */}
      {(step === 'phone' || step === 'sending') && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Add your phone to receive race results by text.
          </p>

          <div>
            <Label htmlFor="verify-phone" className="sr-only">
              Phone number
            </Label>
            <Input
              id="verify-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (415) 555 0123"
              autoComplete="tel"
              disabled={isSending}
              data-testid="verify-phone-input"
            />
          </div>

          {/* Channel toggle */}
          <div className="flex gap-2 text-sm">
            <button
              type="button"
              onClick={() => setChannel('sms')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                channel === 'sms'
                  ? 'bg-navy-900 text-white'
                  : 'border border-gray-300 text-muted-foreground hover:border-navy-400'
              }`}
              data-testid="channel-sms"
            >
              SMS
            </button>
            <button
              type="button"
              onClick={() => setChannel('whatsapp')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                channel === 'whatsapp'
                  ? 'bg-navy-900 text-white'
                  : 'border border-gray-300 text-muted-foreground hover:border-navy-400'
              }`}
              data-testid="channel-whatsapp"
            >
              WhatsApp
            </button>
          </div>

          {/* Turnstile placeholder — renders widget when VITE_TURNSTILE_SITE_KEY is set */}
          {turnstileSiteKey && <div ref={turnstileRef} />}

          {error && (
            <p className="text-sm text-destructive" data-testid="verify-error">{error}</p>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              className="flex-1 bg-brand-blue !text-navy-900 hover:bg-brand-dark"
              onClick={handleSendCode}
              disabled={isSending || !phone}
              data-testid="send-code-button"
            >
              {isSending ? 'Sending…' : 'Send code'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground hover:text-navy-900"
              onClick={onSkip}
              data-testid="verify-skip-button"
            >
              Skip
            </Button>
          </div>
        </div>
      )}

      {/* Code entry */}
      {(step === 'code' || step === 'checking' || step === 'resending') && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Enter the code sent to {maskPhone(phone)}.
          </p>

          <div>
            <Label htmlFor="verify-code" className="sr-only">
              Verification code
            </Label>
            <Input
              id="verify-code"
              ref={codeRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              autoComplete="one-time-code"
              disabled={isChecking || step === 'resending'}
              data-testid="verify-code-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleVerifyCode(); }
              }}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive" data-testid="verify-error">{error}</p>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              className="flex-1 bg-brand-blue !text-navy-900 hover:bg-brand-dark"
              onClick={handleVerifyCode}
              disabled={isChecking || step === 'resending' || code.length < 4}
              data-testid="verify-code-button"
            >
              {isChecking ? 'Verifying…' : 'Verify'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="text-sm text-muted-foreground hover:text-navy-900"
              onClick={() => {
                setStep('resending');
                setError('');
                handleSendCode();
              }}
              disabled={step === 'resending' || isChecking}
              data-testid="resend-code-button"
            >
              {step === 'resending' ? 'Sending…' : 'Resend'}
            </Button>
          </div>

          <button
            type="button"
            className="w-full text-sm text-muted-foreground hover:text-navy-900 hover:underline"
            onClick={() => { setStep('phone'); setError(''); setCode(''); }}
            data-testid="change-phone-button"
          >
            Change phone number
          </button>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg
      className="h-4 w-4 flex-shrink-0 text-green-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

/** Masks a phone number: +14155551234 → +1 (415) ···1234 */
function maskPhone(p: string): string {
  if (!p) return '';
  const digits = p.replace(/\D/g, '');
  if (digits.length < 4) return p;
  return `${p.slice(0, p.length - 7)}···${digits.slice(-4)}`;
}
